import { useQuery, useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useLocation,
	useNavigate,
	useParams,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { AlertCircle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { userQueryOptions } from "@/services/user";
import { getUserInitials } from "@/utils/get-user-initials";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum(["details", "activity"]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform/$userId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			userId: z.string().parse(params.userId),
		}),
	},
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
	beforeLoad: ({ params, location }) => {
		// since we deleted index.tsx
		// Only redirect if we're at the exact parent route (no child route matched)
		const isParentRoute = location.pathname.endsWith(
			`/platform/${params.userId}`,
		);

		if (isParentRoute) {
			throw redirect({
				to: "/admin/users/platform/$userId/details",
				params: { userId: params.userId },
				search: { mode: "view" },
			});
		}
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const _queryErrorResetBoundary = useQueryErrorResetBoundary();
	const _router = useRouter();
	void _queryErrorResetBoundary;
	void _router;
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform/$userId",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform/$userId",
	});
	const [isOpen, setIsOpen] = useState(false);

	const currentTab = location.pathname.includes("/details")
		? "details"
		: "activity";

	const {
		data: userById,
		isLoading,
		error,
	} = useQuery(userQueryOptions.detail(params.userId));
	const { src: userAvatarSrc, isLoading: isAvatarLoading } = useUserAvatarSrc(
		userById?.id,
		userById?.hasAvatar,
	);

	const initials =
		!isAvatarLoading && !userAvatarSrc
			? getUserInitials(userById?.firstName, userById?.lastName)
			: undefined;

	useEffect(() => {
		setIsOpen(true);
	}, []);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: "/admin/users/platform",
				replace: true,
				viewTransition: {
					types: ["navigation", "slide-left"],
				},
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title =
		userById?.displayName ||
		(userById?.firstName && userById?.lastName
			? `${userById.firstName} ${userById.lastName}`
			: t("admin.users.drawer.platform.platformUser"));
	const description = `${t("admin.users.drawer.platform.description")}`;
	const tabs = [
		{ label: t("admin.users.drawer.tabs.details"), id: "details" },
		{ label: t("admin.users.drawer.tabs.activity"), id: "activity" },
	];

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="40%"
			backdropPreset="transparent"
			dialogClassName="gap-0 overflow-hidden"
		>
			{({ close }) => (
				<>
					<Drawer.Header
						variant="document"
						icon={
							<Avatar
								size="md"
								src={userAvatarSrc || undefined}
								initials={initials}
							/>
						}
						iconSize="md"
						iconTheme="light"
						title={<span>{title}</span>}
						description={description}
						showCloseButton
						closeButtonClassName="absolute top-2 right-2 z-10"
						onClose={() => {
							close();
							handleClose();
						}}
					/>
					<Drawer.ScrollBody showPlaceholder={false}>
						{isLoading ? (
							<div className="flex w-full flex-1 items-center justify-center">
								<LoadingIndicator
									type="line-simple"
									size="xl"
									supportingText
								/>
							</div>
						) : error ? (
							<div className="flex flex-col items-center justify-center h-full gap-4">
								<Illustration type="cloud" size="sm">
									<div className="bg-error-500 rounded-full p-3">
										<AlertCircle className="size-6" />
									</div>
								</Illustration>
								<div className="flex flex-col items-center justify-center pt-4">
									<p className="text-gray-900 font-semibold text-md">
										{t("admin.organizations.errors.errorLoadingPage")}
									</p>
									<div className="w-[60%]">
										<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
											{t("admin.organizations.errors.errorLoadingContent")}
										</p>
									</div>
								</div>
							</div>
						) : (
							<div className="flex w-full flex-col">
								<NativeSelect
									aria-label={t("admin.users.tabs.label")}
									value={currentTab}
									onChange={(event) => {
										navigate({
											to: `/admin/users/platform/$userId/${event.target.value}`,
											params: { userId: params.userId },
											search: {
												mode: search.mode,
											},
										});
									}}
									options={tabs.map((tab) => ({
										label: tab.label,
										value: tab.id,
									}))}
									className="mx-6 mt-3 w-80 md:hidden"
								/>
								<div className="flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
									<Tabs
										selectedKey={currentTab}
										onSelectionChange={(event) => {
											navigate({
												to: `/admin/users/platform/$userId/${event}`,
												params: {
													userId: params.userId,
												},
												search: {
													mode: search.mode,
												},
											});
										}}
										className="h-full w-full"
									>
										<TabList
											type="underline"
											size="sm"
											items={tabs}
											className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
										>
											{(tab) => (
												<Tab
													id={tab.id}
													label={tab.label}
													className="h-full items-end -mb-px"
												/>
											)}
										</TabList>
									</Tabs>
								</div>
								<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
									<TabContentWrapper tabKey={currentTab}>
										<Outlet />
									</TabContentWrapper>
								</div>
							</div>
						)}
					</Drawer.ScrollBody>
					<Drawer.Footer>
						<div className="flex items-center justify-end w-full">
							<Button color="secondary" size="md" onClick={handleClose}>
								{t("common.actions.close")}
							</Button>
						</div>
					</Drawer.Footer>
				</>
			)}
		</Drawer>
	);
}
